import React, { useState } from "react";

import CalendarIcon from "@uui/Calendar";
import ChevronDownIcon from "@uui/ChevronDown";
import FileIcon from "@uui/File07";
import { DateTime } from "luxon";

import Collapse from "@mui/material/Collapse";

import StatusChip from "@app/analytics/components/StatusChip";
import { SecondaryTextWithIcon } from "@app/analytics/Pulse/components/styles";
import type { PulseClip } from "@app/analytics/Pulse/types";
import ROUTES from "@app/common/routes";
import type { ActionItemChipStatus } from "@app/common/types";

import Stack, { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import { ListItem, StatusContent, StatusText } from "./styles";

const ActionItemWithStatus = ({
  item,
  onClickChip,
}: {
  item: PulseClip;
  onClickChip: React.MouseEventHandler<HTMLElement>;
}) => {
  const [expanded, setExpanded] = useState(false);

  const expandable = Boolean(item.statusDescription);

  const lastDiscussedDate = item.statusDescription?.startTime
    ? DateTime.fromISO(item.statusDescription.startTime).toRelative()
    : "";

  const canEdit = Boolean(item.canEdit);

  return (
    <ListItem
      spacing={1}
      expandable={expandable}
      expanded={expanded}
      onClick={expandable ? () => setExpanded(!expanded) : undefined}
    >
      <Stack spacing={0.5}>
        <span>
          <TooltipWrapper
            show={!canEdit}
            title="You do not have permission to edit this action item"
          >
            <StatusChip
              canEdit={canEdit}
              status={item.status as ActionItemChipStatus}
              onClick={onClickChip}
            />
          </TooltipWrapper>{" "}
          {item.text}
        </span>
        <Column>
          {item.statusDescription && (
            <Collapse in={expanded}>
              <StatusContent>
                <StatusText>
                  <Bold>Status: </Bold>
                  Last discussed during {item.statusDescription.meetingTitle} (
                  {lastDiscussedDate})
                </StatusText>
                <StatusText>
                  <Bold>Summary: </Bold>
                  {item.statusDescription.text}
                </StatusText>
              </StatusContent>
            </Collapse>
          )}
          <Row spacing={1.5} sx={{ pb: 0.5 }}>
            {item.startTime && (
              <SecondaryTextWithIcon
                Icon={CalendarIcon}
                sx={{ fontWeight: 400, fontSize: "0.625rem" }}
              >
                {item.startTime.toLocaleString({
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </SecondaryTextWithIcon>
            )}
            {item.meetingTitle && (
              <SecondaryTextWithIcon
                Icon={FileIcon}
                href={ROUTES.meetingReportById(item.sessionId)}
                sx={{ fontWeight: 400, fontSize: "0.625rem" }}
              >
                {item.meetingTitle}
              </SecondaryTextWithIcon>
            )}
          </Row>
        </Column>
      </Stack>
      {expandable && (
        <ChevronDownIcon
          sx={{
            fontSize: "16px",
            mt: "2px",
            ...(expanded && { transform: "rotate(180deg)" }),
          }}
        />
      )}
    </ListItem>
  );
};

export default ActionItemWithStatus;
